import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Problema, ProblemaFormData } from '@/types/problema';
import { toast } from 'sonner';
import { useClientFilter } from './useClientFilter';

export type ProblemaOrigem = {
  tipo: 'incidente' | 'requisicao';
  id: string;
  numero: string;
};

export const useProblemas = () => {
  const { currentClientId, showAllClients, clientLoading } = useClientFilter();

  return useQuery({
    queryKey: ['problemas', currentClientId, showAllClients],
    queryFn: async () => {
      let query = supabase
        .from('problemas' as any)
        .select('*');

      if (currentClientId && !showAllClients) {
        query = query.eq('client_id', currentClientId);
      }

      const { data, error } = await query.order('criado_em', { ascending: false });

      if (error) {
        console.error('Erro ao buscar problemas:', error);
        throw error;
      }

      return (data || []) as unknown as Problema[];
    },
    enabled: !clientLoading,
  });
};

export const useCreateProblema = () => {
  const queryClient = useQueryClient();
  const { currentClientId } = useClientFilter();

  return useMutation({
    mutationFn: async ({
      problema: data,
      origem: origemFinal,
    }: {
      problema: ProblemaFormData;
      origem?: ProblemaOrigem;
    }) => {
      const userId = (await supabase.auth.getUser()).data.user?.id;

      // Vincular incidente de origem em incidentes_relacionados (jsonb)
      const incidentesRelacionados =
        origemFinal?.tipo === 'incidente'
          ? [{ id: origemFinal.id, numero: origemFinal.numero }]
          : undefined;



      const payload: any = {
        ...data,
        tipo: 'problema',
        status: data.status || 'aberto',
        criado_por: userId,
        solicitante_id: data.solicitante_id || userId,
        client_id: data.client_id || currentClientId || undefined,
      };
      if (incidentesRelacionados) payload.incidentes_relacionados = incidentesRelacionados;

      const { data: created, error } = await supabase
        .from('problemas' as any)
        .insert([payload])
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar problema:', error);
        throw error;
      }

      const novoProblema = created as any;

      // Log na origem
      if (origemFinal && novoProblema?.id) {
        try {
          const numeroNovo = novoProblema.numero || '';
          const descricaoLog = `Problema ${numeroNovo} aberto a partir deste registro`;
          if (origemFinal.tipo === 'incidente') {
            await supabase.from('incidentes_logs').insert({
              incidente_id: origemFinal.id,
              acao: descricaoLog,
              tipo: 'promocao_problema',
              usuario_id: userId,
            } as any);
          } else {
            await supabase.from('requisicao_logs').insert({
              requisicao_id: origemFinal.id,
              acao: descricaoLog,
              tipo: 'promocao_problema',
              usuario_id: userId,
            } as any);
          }
        } catch (logErr) {
          console.warn('Falha ao registrar log de promoção:', logErr);
        }
      }

      return novoProblema;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['problemas'] });
      toast.success('Problema criado com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao criar problema:', error);
      toast.error('Erro ao criar problema. Tente novamente.');
    },
  });
};

export const useUpdateProblema = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, problema }: { id: string; problema: Partial<ProblemaFormData> }) => {
      const { data, error } = await supabase
        .from('problemas' as any)
        .update({
          ...problema,
          atualizado_em: new Date().toISOString(),
          atualizado_por: (await supabase.auth.getUser()).data.user?.id,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar problema:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['problemas'] });
      toast.success('Problema atualizado com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao atualizar problema:', error);
      toast.error('Erro ao atualizar problema. Tente novamente.');
    },
  });
};

export const useDeleteProblema = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('problemas' as any)
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erro ao deletar problema:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['problemas'] });
      toast.success('Problema deletado com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao deletar problema:', error);
      toast.error('Erro ao deletar problema. Tente novamente.');
    },
  });
};
